/**
 * Utility functions for price input handling
 * These functions handle parsing, validation, and formatting of price inputs
 */

/**
 * Parses a price input string and extracts only numeric digits
 * @param value - The input string (may contain non-numeric characters)
 * @returns A string containing only digits
 */
export function parsePriceInput(value: string): string {
  return value.replace(/[^\d]/g, '') || '0'
}

/**
 * Converts a parsed price string to a numeric value
 * @param cleanedValue - A string containing only digits
 * @returns The numeric value, or 0 if parsing fails
 */
export function parsePriceToNumber(cleanedValue: string): number {
  return parseInt(cleanedValue) || 0
}

/**
 * Validates and enforces min constraint for price input
 * Ensures the value doesn't exceed the maximum allowed value
 * @param numericValue - The numeric value to validate
 * @param maxValue - The maximum allowed value
 * @returns The validated value (clamped to max if necessary)
 */
export function validatePriceMin(
  numericValue: number,
  maxValue: number
): number {
  return Math.min(numericValue, maxValue)
}

/**
 * Validates and enforces max constraint for price input
 * Ensures the value is not less than the minimum allowed value
 * @param numericValue - The numeric value to validate
 * @param minValue - The minimum allowed value
 * @returns The validated value (clamped to min if necessary)
 */
export function validatePriceMax(
  numericValue: number,
  minValue: number
): number {
  return Math.max(numericValue, minValue)
}

/**
 * Complete price parsing and validation pipeline for min price
 * @param inputValue - The raw input string from user
 * @param currentMax - The current maximum price value
 * @returns The validated numeric price value
 */
export function processPriceMinInput(
  inputValue: string,
  currentMax: number
): number {
  const cleanedValue = parsePriceInput(inputValue)
  const numericValue = parsePriceToNumber(cleanedValue)
  return validatePriceMin(numericValue, currentMax)
}

/**
 * Complete price parsing and validation pipeline for max price
 * @param inputValue - The raw input string from user
 * @param currentMin - The current minimum price value
 * @returns The validated numeric price value
 */
export function processPriceMaxInput(
  inputValue: string,
  currentMin: number
): number {
  const cleanedValue = parsePriceInput(inputValue)
  const numericValue = parsePriceToNumber(cleanedValue)
  return validatePriceMax(numericValue, currentMin)
}
